/**
 * Sprint T-18 · Notification Studio · Analytics · Throughput chart (SVG puro).
 *
 * Line + area chart. No dependencies. Handles gaps by rendering 0.
 * Autoscales vertically. Range: `points` from analytics/throughput endpoint.
 */
import React, { useMemo } from "react";

const W = 720;
const H = 200;
const PAD = 24;

export default function AnalyticsThroughputChart({ data, loading }) {
  const points = data?.points || [];

  const geom = useMemo(() => {
    if (points.length === 0) return null;
    const max = Math.max(1, ...points.map((p) => p.count));
    const step = points.length > 1 ? (W - 2 * PAD) / (points.length - 1) : 0;
    const yFor = (v) => H - PAD - ((v / max) * (H - 2 * PAD));
    const pathTotal = points
      .map((p, i) => `${i === 0 ? "M" : "L"}${PAD + i * step},${yFor(p.count)}`)
      .join(" ");
    const pathSent = points
      .map((p, i) => `${i === 0 ? "M" : "L"}${PAD + i * step},${yFor(p.sent)}`)
      .join(" ");
    const areaTotal = `${pathTotal} L${PAD + (points.length - 1) * step},${H - PAD} L${PAD},${H - PAD} Z`;
    return { max, step, yFor, pathTotal, pathSent, areaTotal };
  }, [points]);

  return (
    <div
      className="rounded border border-[var(--ag-border)] bg-[var(--ag-panel)] p-3"
      data-testid="studio-analytics-throughput-chart"
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-[var(--ag-text-dim)]">
          Throughput · {data?.bucket === "1d" ? "daily" : "hourly"} · {points.length} buckets
        </div>
        <div className="flex items-center gap-2 text-[10px] text-[var(--ag-text-dim)]">
          <span className="flex items-center gap-1">
            <span className="inline-block h-1 w-3 rounded bg-[var(--ag-gold)]" /> total
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-1 w-3 rounded bg-emerald-400" /> sent
          </span>
        </div>
      </div>
      {loading && !data ? (
        <div className="py-8 text-center text-xs text-[var(--ag-text-dim)]">
          cargando…
        </div>
      ) : points.length === 0 ? (
        <div
          className="py-8 text-center text-xs text-[var(--ag-text-dim)]"
          data-testid="studio-analytics-throughput-empty"
        >
          Sin datos en el rango seleccionado.
        </div>
      ) : (
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="h-40 w-full"
        >
          {/* Grid horizontal */}
          {[0.25, 0.5, 0.75].map((frac) => (
            <line
              key={frac}
              x1={PAD}
              y1={H - PAD - frac * (H - 2 * PAD)}
              x2={W - PAD}
              y2={H - PAD - frac * (H - 2 * PAD)}
              stroke="currentColor"
              strokeOpacity={0.1}
              strokeDasharray="2 4"
            />
          ))}
          {/* Area total */}
          <path d={geom.areaTotal} fill="url(#gradTotal)" opacity="0.35" />
          {/* Line total */}
          <path d={geom.pathTotal} fill="none" stroke="var(--ag-gold)" strokeWidth={1.5} />
          {/* Line sent */}
          <path d={geom.pathSent} fill="none" stroke="rgb(52,211,153)" strokeWidth={1.5} />
          {/* Points hoverable */}
          {points.map((p, i) => (
            <circle
              key={i}
              cx={PAD + i * geom.step}
              cy={geom.yFor(p.count)}
              r={2}
              fill="var(--ag-gold)"
            >
              <title>{`${p.t} · total=${p.count} · sent=${p.sent}`}</title>
            </circle>
          ))}
          <defs>
            <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--ag-gold)" stopOpacity="0.6" />
              <stop offset="100%" stopColor="var(--ag-gold)" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>
      )}
    </div>
  );
}
